// Packages
import { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
// Modules
import webStorage from 'modules/storage';
import sessionStorageApp from 'modules/storage/sessionStorage';
import errorHandler from 'modules/error';
import { isAccessTokenExpiringSoon } from './tokenExpiry';
import { refreshAccessToken, scheduleTokenRefresh } from './tokenRefresh';
// I18n
import { getCurrentLanguage } from 'i18n';

//* Marks a request that has already been retried once after a 401, so a token that
//* is still rejected after a fresh refresh can't cause an infinite refresh/retry loop.
interface IRetriableRequestConfig extends AxiosRequestConfig {
  _isRetryAfterRefresh?: boolean;
}

//* On request (success) handler
const onRequest = async (config: AxiosRequestConfig): Promise<AxiosRequestConfig> => {
  const storageData = webStorage.getData();
  const sessionStorageData = sessionStorageApp.getData();
  const language = getCurrentLanguage();

  let accessToken = storageData?.accessToken;

  //* Catches a session this tab just stored (sign-in, account switch): no storage event fires here.
  scheduleTokenRefresh();

  //* Proactively refresh the access token before it expires so requests don't fail with
  //* 401 and get transparently retried. Only the main session (with a refresh token) is
  //* refreshable — the two-factor session token below is intentionally left untouched.
  if (accessToken && storageData?.refreshToken && isAccessTokenExpiringSoon(accessToken)) {
    try {
      accessToken = await refreshAccessToken(accessToken);
    } catch {
      //* Refresh failed — refreshAccessToken already notified and scheduled the redirect.
      //* Fall through with the stale token; the reactive 401 handler is the final fallback.
    }
  }

  if (accessToken) {
    config!.headers!.Authorization = `Bearer ${accessToken}`;
  }

  // two factor auth
  if (!accessToken && sessionStorageData?.accessToken) {
    config!.headers!.Authorization = `Bearer ${sessionStorageData.accessToken}`;
  }

  config.headers['Accept-Language'] = language;

  return config;
};

//* On request (error) handler
const onRequestError = (error: AxiosError): Promise<AxiosError> => {
  return Promise.reject(error);
};

//* On response (success) handler
const onResponse = (response: AxiosResponse): AxiosResponse['data'] => {
  return response.data;
};

//* On response (error) handler — reactive fallback: refresh once on 401 and retry the request
const onResponseError = (axiosInstance: AxiosInstance) => async (error: AxiosError) => {
  await errorHandler.observe(error);

  const originalRequest = error.config as IRetriableRequestConfig | undefined;

  if (
    error.response?.status !== 401 ||
    !originalRequest ||
    originalRequest._isRetryAfterRefresh
  ) {
    return Promise.reject(error);
  }

  originalRequest._isRetryAfterRefresh = true;

  const rejectedAccessToken = String(originalRequest.headers?.Authorization ?? '').replace(
    'Bearer ',
    '',
  );

  try {
    const accessToken = await refreshAccessToken(rejectedAccessToken);

    originalRequest.headers!.Authorization = `Bearer ${accessToken}`;

    return axiosInstance(originalRequest);
  } catch (refreshError) {
    return Promise.reject(refreshError);
  }
};

//* Setup interceptors to the axios instance
const setupInterceptorsTo = (axiosInstance: AxiosInstance): AxiosInstance => {
  axiosInstance.interceptors.request.use(onRequest, onRequestError);
  axiosInstance.interceptors.response.use(onResponse, onResponseError(axiosInstance));

  return axiosInstance;
};

export default setupInterceptorsTo;
